import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Grid,
  Table,
  TableRow
} from "semantic-ui-react";
import { AccordionTitleView, CommonTable, CustomButton, DropDown } from "../../../components";
import { InputNumber } from "../../../components/InputText/InputNumber";
import { FacilityHeatTable, HEAT_SOURCE_UNIT_TYPES, UNIT_TYPES } from "../../../config/constants";
import { DEFAULT_HEAT_SOURCE } from "../../../config/drawingConstants";
import "../facilityForms/facilityForm.scss";

const FacilityHeatSourceSimulation = ({
  heatType,
  currentFacilityHeatData,
  originalHeatSourceData,
  setDeleteHeatData,
  setVisibleDeleteHeatModal,
  renderTableData = false,
  isFacilitySimulation,
  isViewMode = false
}: any) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [defaultHeatType, setDefaultHeatType] = useState<number>(DEFAULT_HEAT_SOURCE);
  const [getDefaultUnit, setDefaultUnit] = useState<string>(UNIT_TYPES[0].value);
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const {
    formState: { errors: errorsFacilityHeat },
    handleSubmit: handleSubmitFacilityHeat,
    setValue,
    reset,
    control
  } = useForm();

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const selectedHeatType = useMemo(() => {
    return heatType?.find((item: any) => item?.value === String(defaultHeatType));
  }, [heatType, defaultHeatType]);

  const addFacilityHeatData = (data: any) => {
    const price = data?.Price ? Number(data?.Price) : 0;
    let updateData = {
      ...data,
      Price: price,
      UnitType: getDefaultUnit,
      Volumes: Number(data?.Volumes),
      HeatSource: Number(defaultHeatType) || DEFAULT_HEAT_SOURCE
    };
    if (enableEdit && editIndex !== null) {
      currentFacilityHeatData[editIndex] = updateData;
    } else {
      currentFacilityHeatData.push(updateData);
    }
    reset();
    setDefaultHeatType(DEFAULT_HEAT_SOURCE);
    setDefaultUnit(UNIT_TYPES[0].value);
    setEditIndex(null);
    setEnableEdit(false);
  };

  const groupHeatData = (data: any) => {
    let groupIngredientData = data?.reduce((currentType: any, data: any, index: any) => {
      data.index = index;
      let checkDataIsAvailable = currentType?.find((other: any) => other.key === data.Year);
      if (currentType?.length === 0) {
        let facilityData = {
          key: data.Year,
          value: [data]
        };
        return [...currentType, facilityData];
      } else if (checkDataIsAvailable) {
        let currentIndex = currentType?.findIndex((other: any) => other.key === data.Year);
        let updatedArray = [];

        const updateArray = [...currentType[currentIndex].value, data];
        const updatedObject = {
          ...currentType[currentIndex],
          value: updateArray
        };
        updatedArray = [
          ...currentType.slice(0, currentIndex),
          updatedObject,
          ...currentType.slice(currentIndex + 1)
        ];
        return updatedArray;
      } else {
        let facilityData = {
          key: data.Year,
          value: [data]
        };

        return [...currentType, facilityData];
      }
    }, []);
    return groupIngredientData;
  };

  const setFacilityHeatDetails = (data: any) => {
    setValue("Percentage", data?.Percentage);
    setValue("Price", data?.Price);
    setValue("Year", data?.Year);
    setValue("Volumes", data?.Volumes);
    setDefaultUnit(data?.UnitType);
    setDefaultHeatType(data?.HeatSource);
    setEditIndex(data?.index);
    setEnableEdit(true);
  };

  const renderTableRow = (
    dataView: any,
    rowSpan: any,
    totalValue: any,
    onDelete: any,
    showIcons: boolean
  ) => {
    const heatTypeAsText = heatType?.find(
      (item: any) => item?.value === String(dataView?.HeatSource)
    )?.text;
    return (
      <TableRow key={dataView?.id || dataView?.index}>
        {rowSpan > 0 && (
          <Table.Cell rowSpan={rowSpan}>
            <p>{dataView?.Year ?? ""}</p>
          </Table.Cell>
        )}
        <Table.Cell>
          <p>{heatTypeAsText ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>
            {dataView?.Volumes ?? ""}
            <span className="unitTypeView">{dataView?.UnitType ?? ""}</span>
          </p>
        </Table.Cell>
        <Table.Cell>
          <p>{dataView?.Price?.toFixed(2) ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{totalValue?.toFixed(2)}</p>
        </Table.Cell>
        {!showIcons && !isViewMode ? (
          <Table.Cell>
            <Grid>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdModeEditOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => setFacilityHeatDetails(dataView)}
                />
              </Grid.Column>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={onDelete}
                />
              </Grid.Column>
            </Grid>
          </Table.Cell>
        ) : null}
      </TableRow>
    );
  };

  const loadOriginalTableData = () => {
    const groupFacilityByYear = groupHeatData(originalHeatSourceData);
    return groupFacilityByYear?.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const Volumes = dataView?.Volumes || 0;
        const price = dataView?.Price || 0;
        const totalValue = Volumes * price;

        const handleDelete = () => {
          setDeleteHeatData(dataView);
          setVisibleDeleteHeatModal(true);
        };
        return renderTableRow(dataView, isFirstRow ? rowCount : 0, totalValue, handleDelete, true);
      });
    });
  };

  const loadTableData = () => {
    const groupFacilityByYear = groupHeatData(currentFacilityHeatData);
    return groupFacilityByYear?.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const Volumes = dataView?.Volumes || 0;
        const price = dataView?.Price || 0;
        const totalValue = Volumes * price;

        const handleDelete = () => {
          setDeleteHeatData(dataView);
          setVisibleDeleteHeatModal(true);
        };
        return renderTableRow(
          dataView,
          isFirstRow ? rowCount : 0,
          totalValue,
          handleDelete,
          renderTableData
        );
      });
    });
  };

  return (
    <>
      <Accordion>
        <AccordionTitle active={accordionEnabled} onClick={handleClick}>
          <AccordionTitleView accordionEnabled={accordionEnabled} title={"Heat Sources"} />
        </AccordionTitle>
        <AccordionContent active={accordionEnabled}>
          <Grid>
            <Grid.Row divided>
              {isFacilitySimulation ? (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  {originalHeatSourceData?.length > 0 && (
                    <CommonTable
                      tableHeaderData={FacilityHeatTable.filter((e: any) => e.name !== "")}
                      isSimulateView
                    >
                      {loadOriginalTableData()}
                    </CommonTable>
                  )}
                </Grid.Column>
              ) : null}
              <Grid.Column
                computer={isFacilitySimulation ? 8 : 16}
                tablet={16}
                mobile={16}
                className={!renderTableData ? "userBankDetailsMain" : "recipeTableViewMain"}
              >
                <div>
                  {!renderTableData ? (
                    <Grid>
                      <Grid.Column
                        computer={4}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      >
                        <DropDown
                          labelName={"Source*"}
                          placeholder="Heat Source"
                          currentData={heatType}
                          defaultValue={selectedHeatType?.value || null}
                          customGridColumn={"customGridColomnType"}
                          handleChangeState={(e: any, { value }: any) => {
                            setDefaultHeatType(value);
                          }}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column
                        computer={4}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      >
                        <InputNumber
                          control={control}
                          errors={errorsFacilityHeat.Year}
                          labelName={"Year*"}
                          placeholder="Year"
                          name="Year"
                          required={true}
                          errorMessage="Year is required"
                          isYearInput={true}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column
                        computer={8}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      />
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityHeat.Volumes}
                          labelName={"Volumes consumed*"}
                          placeholder="Volumes"
                          name="Volumes"
                          required={true}
                          errorMessage="Volumes is required"
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={2} tablet={8} mobile={16}>
                        <Grid.Column computer={4} tablet={8} mobile={16}>
                          <DropDown
                            labelName={"Unit Type*"}
                            placeholder="Type"
                            currentData={HEAT_SOURCE_UNIT_TYPES}
                            defaultValue={getDefaultUnit}
                            customGridColumn={"customGridColomnType"}
                            handleChangeState={(e: any, { value }: any) => {
                              setDefaultUnit(value);
                            }}
                            disabled={isViewMode}
                          />
                        </Grid.Column>
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityHeat.Price}
                          labelName={"Price per unit"}
                          placeholder="Price"
                          name="Price"
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        {enableEdit ? (
                          <CustomButton
                            theme="green"
                            customColumnStyle={"addUnitSize"}
                            title="Update"
                            onClick={handleSubmitFacilityHeat((data: any) => {
                              addFacilityHeatData(data);
                            })}
                            disabled={isViewMode}
                          />
                        ) : (
                          <CustomButton
                            theme="green"
                            customColumnStyle={"addUnitSize"}
                            icon="plus"
                            onClick={handleSubmitFacilityHeat((data: any) => {
                              addFacilityHeatData(data);
                            })}
                            disabled={isViewMode}
                          />
                        )}
                      </Grid.Column>
                    </Grid>
                  ) : null}

                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    className={!renderTableData ? "productRegisterTable customTableBottom" : ""}
                  >
                    {currentFacilityHeatData?.length > 0 && (
                      <CommonTable tableHeaderData={FacilityHeatTable}>
                        {loadTableData()}
                      </CommonTable>
                    )}
                  </Grid.Column>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </AccordionContent>
      </Accordion>
    </>
  );
};

export default FacilityHeatSourceSimulation;
